import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  dispatchApi,
  type AcknowledgeReceiptRequest,
  type ApproveCompensationNoteRequest,
  type CancelDispatchRequest,
  type CompleteDispatchRequest,
  type ConfirmDeliveryRequest,
  type ConfirmHandoverRequest,
  type InitiateDispatchRequest,
  type RaiseCompensationNoteRequest,
  type RecordTrayReturnRequest,
  type RejectCompensationNoteRequest,
} from "../services/dispatchApi";

// ── Query Keys ─────────────────────────────────────────────────────────────

const QK = {
  dispatch: (id: string) => ["dispatch", "detail", id],
  byOrder: (orderId: string) => ["dispatch", "order", orderId],
  pending: (page: number, size: number, search?: string) => ["dispatch", "pending", page, size, search ?? ""],
  readyForHandover: ["dispatch", "ready-for-handover"],
  readyForRelease: ["dispatch", "ready-for-release"],
  readyForHandoverPaged: (page: number, size: number, search?: string) => ["dispatch", "ready-for-handover", page, size, search ?? ""],
  readyForReleasePaged: (page: number, size: number, search?: string) => ["dispatch", "ready-for-release", page, size, search ?? ""],
  dispatched: ["dispatch", "dispatched"],
  dispatchHistoryPaged: (page: number, size: number, search?: string) => ["dispatch", "history-paged", page, size, search ?? ""],
  trayReturnsHistoryPaged: (page: number, size: number, search?: string) => ["dispatch", "tray-returns-history", page, size, search ?? ""],
  preparation: (orderId: string) => ["dispatch", "preparation", orderId],
  outstandingTrays: (county?: string) => ["dispatch", "outstanding-trays", county ?? "all"],
  outstandingTraysPaged: (page: number, size: number, search?: string, county?: string) => ["dispatch", "outstanding-trays-paged", page, size, search ?? "", county ?? "all"],
  deliveryNote: (id: string) => ["dispatch", "delivery-note", id],
  compensationDetail: (id: string) => ["compensation-notes", "detail", id],
  compensationByOrder: (orderId: string) => ["compensation-notes", "order", orderId],
  pendingCompensations: ["compensation-notes", "pending"],
} as const;

// ── Dispatch Queries ───────────────────────────────────────────────────────

export const useDispatchById = (id?: string) =>
  useQuery({
    queryKey: QK.dispatch(id!),
    queryFn: () => dispatchApi.getById(id!),
    enabled: !!id,
  });

export const useDispatchByOrder = (orderId?: string) =>
  useQuery({
    queryKey: QK.byOrder(orderId!),
    queryFn: () => dispatchApi.getByOrder(orderId!),
    enabled: !!orderId,
  });

export const useDispatchPreparation = (orderId?: string, enabled = true) =>
  useQuery({
    queryKey: QK.preparation(orderId!),
    queryFn: () => dispatchApi.getPreparation(orderId!),
    enabled: !!orderId && enabled,
  });

export const usePendingDispatch = (pageNumber: number, pageSize: number, search?: string) =>
  useQuery({
    queryKey: QK.pending(pageNumber, pageSize, search),
    queryFn: () => dispatchApi.getPending(pageNumber, pageSize, search),
  });

export const useReadyForHandover = (pageNumber: number, pageSize: number, search?: string) =>
  useQuery({
    queryKey: QK.readyForHandoverPaged(pageNumber, pageSize, search),
    queryFn: () => dispatchApi.getReadyForHandover(pageNumber, pageSize, search),
  });

export const useReadyForRelease = (pageNumber: number, pageSize: number, search?: string) =>
  useQuery({
    queryKey: QK.readyForReleasePaged(pageNumber, pageSize, search),
    queryFn: () => dispatchApi.getReadyForRelease(pageNumber, pageSize, search),
  });

export const useDispatchedRecords = () =>
  useQuery({
    queryKey: QK.dispatched,
    queryFn: dispatchApi.getDispatched,
  });

export const useDispatchHistoryPaged = (pageNumber: number, pageSize: number, search?: string) =>
  useQuery({
    queryKey: QK.dispatchHistoryPaged(pageNumber, pageSize, search),
    queryFn: () => dispatchApi.getDispatchHistoryPaged(pageNumber, pageSize, search),
  });

export const useTrayReturnsHistoryPaged = (pageNumber: number, pageSize: number, search?: string) =>
  useQuery({
    queryKey: QK.trayReturnsHistoryPaged(pageNumber, pageSize, search),
    queryFn: () => dispatchApi.getTrayReturnsHistoryPaged(pageNumber, pageSize, search),
  });

export const useOutstandingTrays = (county?: string) =>
  useQuery({
    queryKey: QK.outstandingTrays(county),
    queryFn: () => dispatchApi.getOutstandingTrays(county),
  });

export const useOutstandingTraysPaged = (pageNumber: number, pageSize: number, search?: string, county?: string) =>
  useQuery({
    queryKey: QK.outstandingTraysPaged(pageNumber, pageSize, search, county),
    queryFn: () => dispatchApi.getOutstandingTraysPaged(pageNumber, pageSize, search, county),
  });

export const useDeliveryNote = (id?: string) =>
  useQuery({
    queryKey: QK.deliveryNote(id!),
    queryFn: () => dispatchApi.getDeliveryNote(id!),
    enabled: !!id,
  });

// ── Dispatch Mutations ─────────────────────────────────────────────────────

export const useInitiateDispatch = () =>
  useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: InitiateDispatchRequest; idempotencyKey: string }) =>
      dispatchApi.initiate(data, idempotencyKey),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: QK.readyForHandover });
      queryClient.invalidateQueries({ queryKey: ["dispatch", "pending"] });
      queryClient.invalidateQueries({ queryKey: QK.byOrder(vars.data.orderId) });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
      queryClient.invalidateQueries({ queryKey: ["dispatch-execution-queue"] });
      queryClient.invalidateQueries({ queryKey: ["delivery-plans"] });
    },
  });

export const useConfirmHandover = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConfirmHandoverRequest }) =>
      dispatchApi.confirmHandover(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: QK.dispatch(id) });
      queryClient.invalidateQueries({ queryKey: ["dispatch", "pending"] });
      queryClient.invalidateQueries({ queryKey: QK.readyForHandover });
      // Stock is committed at acknowledgement — refresh greenhouse stock surfaces
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["customer-collections"] });
    },
  });

export const useAcknowledgeReceipt = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: AcknowledgeReceiptRequest }) =>
      dispatchApi.acknowledgeReceipt(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: QK.dispatch(id) });
      queryClient.invalidateQueries({ queryKey: QK.readyForHandover });
      queryClient.invalidateQueries({ queryKey: QK.readyForRelease });
      queryClient.invalidateQueries({ queryKey: ["customer-collections"] });
    },
  });

export const useCompleteDispatch = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: CompleteDispatchRequest }) =>
      dispatchApi.complete(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: QK.dispatch(id) });
      queryClient.invalidateQueries({ queryKey: QK.readyForRelease });
      queryClient.invalidateQueries({ queryKey: ["dispatch", "outstanding-trays"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
    },
  });

export const useConfirmDelivery = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConfirmDeliveryRequest }) =>
      dispatchApi.confirmDelivery(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: QK.dispatch(id) });
      queryClient.invalidateQueries({ queryKey: ["dispatch", "outstanding-trays"] });
    },
  });

export const useRecordTrayReturn = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: RecordTrayReturnRequest }) =>
      dispatchApi.recordTrayReturn(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: QK.dispatch(id) });
      queryClient.invalidateQueries({ queryKey: ["dispatch", "outstanding-trays"] });
      queryClient.invalidateQueries({ queryKey: ["dispatch", "tray-returns-history"] });
      queryClient.invalidateQueries({ queryKey: ["dispatch", "pending-tray-returns"] });
    },
  });

export const usePendingTrayReturns = () =>
  useQuery({
    queryKey: ["dispatch", "pending-tray-returns"],
    queryFn: () => dispatchApi.getPendingTrayReturns(),
  });

export const useApproveTrayReturn = () =>
  useMutation({
    mutationFn: ({ id, approvalNotes }: { id: string; approvalNotes?: string }) =>
      dispatchApi.approveTrayReturn(id, approvalNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dispatch", "pending-tray-returns"] });
      queryClient.invalidateQueries({ queryKey: ["dispatch", "tray-returns-history"] });
      queryClient.invalidateQueries({ queryKey: ["dispatch", "outstanding-trays"] });
    },
  });

export const useRejectTrayReturn = () =>
  useMutation({
    mutationFn: ({ id, rejectionReason }: { id: string; rejectionReason: string }) =>
      dispatchApi.rejectTrayReturn(id, rejectionReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dispatch", "pending-tray-returns"] });
      queryClient.invalidateQueries({ queryKey: ["dispatch", "tray-returns-history"] });
      queryClient.invalidateQueries({ queryKey: ["dispatch", "outstanding-trays"] });
    },
  });

export const useCancelDispatch = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: CancelDispatchRequest }) =>
      dispatchApi.cancel(id, data),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: QK.dispatch(id) });
      queryClient.invalidateQueries({ queryKey: ["dispatch", "pending"] });
      queryClient.invalidateQueries({ queryKey: QK.byOrder(data.orderId) });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
    },
  });

// ── Compensation Note Queries ──────────────────────────────────────────────

export const useCompensationById = (id?: string) =>
  useQuery({
    queryKey: QK.compensationDetail(id!),
    queryFn: () => dispatchApi.getCompensationById(id!),
    enabled: !!id,
  });

export const useCompensationByOrder = (orderId?: string) =>
  useQuery({
    queryKey: QK.compensationByOrder(orderId!),
    queryFn: () => dispatchApi.getCompensationByOrder(orderId!),
    enabled: !!orderId,
  });

export const usePendingCompensations = () =>
  useQuery({
    queryKey: QK.pendingCompensations,
    queryFn: dispatchApi.getPendingCompensations,
  });

// ── Compensation Note Mutations ────────────────────────────────────────────

export const useRaiseCompensation = () =>
  useMutation({
    mutationFn: (data: RaiseCompensationNoteRequest) => dispatchApi.raiseCompensation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["compensation-notes"] });
    },
  });

export const useSubmitCompensation = () =>
  useMutation({
    mutationFn: (id: string) => dispatchApi.submitCompensation(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: QK.compensationDetail(id) });
      queryClient.invalidateQueries({ queryKey: QK.pendingCompensations });
    },
  });

export const useApproveCompensation = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: ApproveCompensationNoteRequest }) =>
      dispatchApi.approveCompensation(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: QK.compensationDetail(id) });
      queryClient.invalidateQueries({ queryKey: QK.pendingCompensations });
    },
  });

export const useRejectCompensation = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: RejectCompensationNoteRequest }) =>
      dispatchApi.rejectCompensation(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: QK.compensationDetail(id) });
      queryClient.invalidateQueries({ queryKey: QK.pendingCompensations });
    },
  });
